import {useCallback} from 'react';

import {
  useUpdateUserInfoMutation,
  useViewerUserInfoQuery,
} from '../../generated/graphql';
import {Analytics} from '../../services/analytics';
import {UserInfo} from '../../types/graphql';
import {CustomBetaFeature} from '../betaFeatures';
import {CUSTOM_DEFAULT_ORG_FLAGS} from './customDefaultFlags';

export interface CustomDefaultMetadata {
  featureKey: CustomDefaultKeys; // unique id of the feature, e.g. 'model-registry'
  name: string; // human-readable name of the feature
  description: string; // human-readable description of the feature
  docUrl?: string; // link to the docs for the feature
}

export type CustomDefaultKeys = keyof typeof CUSTOM_DEFAULT_ORG_FLAGS;

export function useCustomDefault(
  featureKey: CustomDefaultKeys
): CustomBetaFeature {
  const viewerUserInfo = useViewerUserInfoQuery();
  const userInfo = viewerUserInfo?.data?.viewer?.userInfo;
  const userInfoBetaFeatures: UserInfo['betaFeatures'] = userInfo?.betaFeatures;

  const [updateUserInfo] = useUpdateUserInfoMutation();

  const userSelectedValue = userInfoBetaFeatures?.[featureKey];

  const updateBetaFeature = useCallback(
    (newVal: string | number) => {
      if (userInfo == null) {
        console.error(
          'Attempting to update user beta feature without first loading user info'
        );
        return;
      }

      Analytics.track(`User configured beta feature ${featureKey}: ${newVal}`, {
        newVal,
      });
      updateUserInfo({
        variables: {
          userInfo: JSON.stringify({
            ...userInfo,
            betaFeatures: {
              ...(userInfoBetaFeatures ?? {}),
              [featureKey]: newVal,
            },
          }),
        },
      });
    },
    [featureKey, updateUserInfo, userInfo, userInfoBetaFeatures]
  );

  return {
    updateBetaFeature,
    userSelectedValue,
  };
}

export const CUSTOM_USER_DEFAULTS: ReadonlyArray<CustomDefaultMetadata> = [
  {
    featureKey: 'custom-max-runs',
    name: 'Max runs (workspaces)',
    description:
      'Configure the default number of runs to show in a chart when grouping is inactive',
  },
];
